import { useCallback, useEffect, useRef } from "react";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { getProgress, saveProgress, type ProgressSave } from "../api";
import { useSession } from "../session";

/** One save: where the reader is, in the chapter's published edition. */
export type ProgressUpdate = ProgressSave;

/**
 * placeSaved marks stale what a written place changes: the chapter's own
 * place, and the Library's "Pick up where you left off" card and shelf bars.
 * Those are kept for thirty seconds (main.tsx), so without this Back to the
 * Library showed the place the reader had before (owner, 10-17).
 */
export function placeSaved(client: QueryClient, chapterID: string) {
  return Promise.all([
    client.invalidateQueries({ queryKey: ["progress", chapterID] }),
    client.invalidateQueries({ queryKey: ["continue"] }),
    // The book page's ticks (ADR-0187); the chapter's book is not known here.
    client.invalidateQueries({ queryKey: ["book-progress"] }),
    client.invalidateQueries({ queryKey: ["library"] }),
  ]);
}

/**
 * useProgressWriter is the client's one way of keeping a reader's place
 * (ADR-0058): the player's position and a text chapter's fraction both go
 * through it, so the throttle, who saves and sticky completion are one piece.
 *
 * `enabled` is the reader page. Progress is the reader's place in the
 * published chapter, and the studio plays the working copy's audio, whose
 * positions do not describe it.
 *
 * Throttled to about one write every four seconds. The timer holds the most
 * recent update rather than queueing them, so stopping mid-window still saves
 * where the reader actually stopped.
 */
export function useProgressWriter(chapterID: string, enabled: boolean) {
  const client = useQueryClient();
  const { me } = useSession();
  const active = enabled && !!me;

  const saved = useQuery({
    queryKey: ["progress", chapterID],
    queryFn: () => getProgress(chapterID),
    enabled: active,
    retry: false,
  });

  const pending = useRef<ProgressUpdate | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(() => {
    const next = pending.current;
    pending.current = null;
    timer.current = null;
    if (!next || !active) return;
    void saveProgress(chapterID, next).then(() => placeSaved(client, chapterID));
  }, [active, chapterID, client]);

  const record = useCallback(
    (update: ProgressUpdate) => {
      if (!active) return;
      // Completion is sticky on the server; kept sticky here too, so a later
      // update in the same window cannot drop a completion before it is sent.
      // Having read to the end likewise.
      const completed = update.completed || !!pending.current?.completed;
      const read_to_end = !!update.read_to_end || !!pending.current?.read_to_end;
      pending.current = { ...update, completed, read_to_end };
      if (timer.current) return;
      timer.current = setTimeout(flush, 4000);
    },
    [active, flush],
  );

  // Anything in flight when the page closes is written immediately: a place
  // lost on navigation is the one the reader most wants back.
  useEffect(() => {
    const onHide = () => {
      if (timer.current) clearTimeout(timer.current);
      flush();
    };
    window.addEventListener("pagehide", onHide);
    return () => {
      window.removeEventListener("pagehide", onHide);
      onHide();
    };
  }, [flush]);

  return {
    /** The saved place; undefined until known, or with nothing to know. */
    saved: saved.data,
    /** Whether there is nothing more to wait for before resuming. */
    settled: !active || !saved.isPending,
    record,
  };
}
